import type { GeoJSON } from 'geojson';
import type {
  LayerSlice,
  GeoDataStateCreator,
  LayerBase,
  GeoDataState,
  BBox,
  SourceSpecificationUnion,
  RasterSourceSpecification,
  RasterDemSourceSpecification,
  VectorSourceSpecification,
  GeoJSONSourceSpecification,
  // VideoSourceSpecification, // Non più necessario qui
  // ImageSourceSpecification, // Non più necessario qui
  // Aggiunto LayerPropertyKeysPayload per la nuova azione
  LayerPropertyKeysPayload,
} from './types';
import { cleanSourceOptions } from './utils'; // Importa la funzione di utilità

// Estrai le chiavi univoche dalle proprietà di tutte le feature
export const extractPropertyKeys = (featureCollection: GeoJSON.FeatureCollection): string[] => {
  const allPropertyKeys = new Set<string>();
  featureCollection.features.forEach(feature => {
    if (feature?.properties) {
      Object.keys(feature.properties).forEach(key => {
        allPropertyKeys.add(key);
      });
    }
  });
  return Array.from(allPropertyKeys);
};

export const createLayerSlice: GeoDataStateCreator<LayerSlice> = (set, get) => ({
  dataLayers: [],
  selectedLayerId: null, // Aggiunto stato per ID layer selezionato
  addLayer: (
    data: GeoJSON | undefined, // Mantenuto opzionale per supportare raster
    name: string,
    maplibreLayerType: LayerBase['maplibreLayerType'], // Tipo aggiornato in types.ts
    originalFormat?: string,
    maplibreSourceId?: string, // Mantenuto per raster
    extent?: BBox, // Mantenuto
    maplibreLayerId?: string // Mantenuto per raster
  ): string => { // Assicura che il tipo di ritorno sia string

    let featureCollection: GeoJSON.FeatureCollection | undefined = undefined;
    let availableKeys: string[] = [];
    let newLayerId: string;
    let sourceSpecification: SourceSpecificationUnion; // Modificato: deve essere sempre inizializzata

    // Gestione ID e dati specifici per tipo
    if (maplibreLayerType === 'raster') {
      // Per layer raster (es. WMS aggiunti tramite questa funzione base, o futuri raster locali)
      newLayerId = maplibreLayerId || `raster-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      sourceSpecification = {
        type: 'raster',
        // Per default, tileSize può essere 256 o 512. MapLibre di default è 512.
        // Se non specificato qui, MapLibre userà il suo default.
        // Lo aggiungiamo qui per coerenza se necessario, o lo lasciamo gestire a MapLibre.
        // Per ora, lo lasciamo non specificato qui, sarà aggiunto/modificato tramite UI.
      } as RasterSourceSpecification;
      // console.warn(
      //   `[layerSlice/addLayer] Aggiunto layer raster '${name}' (${newLayerId}) usando 'addLayer'. ` +
      //   `La sourceSpecification potrebbe essere incompleta (mancano url/tiles). ` +
      //   `Considerare l'uso di 'addRasterSourceAndLayer' per sorgenti raster.`
      // );
    } else if (data) {
      // Per tutti gli altri tipi basati su GeoJSON ('fill', 'line', 'circle', etc.)
      newLayerId = maplibreLayerId || `geojson-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

      if (data.type === 'FeatureCollection') {
        featureCollection = data;
      } else if (data.type === 'Feature') {
        featureCollection = { type: 'FeatureCollection', features: [data] };
      } else { // Geometry
        featureCollection = {
          type: 'FeatureCollection',
          features: [{ type: 'Feature', geometry: data, properties: {} }],
        };
      }
      availableKeys = extractPropertyKeys(featureCollection);
      sourceSpecification = {
        type: 'geojson',
        data: featureCollection, // GeoJSON inline
      } as GeoJSONSourceSpecification; // Cast esplicito per assicurare il tipo corretto
    } else {
      console.error(
        `Errore: Tentativo di aggiungere un layer di tipo '${maplibreLayerType}' senza fornire dati GeoJSON e non essendo raster.`
      );
      sourceSpecification = { type: 'geojson', data: {type: 'FeatureCollection', features: []} } as GeoJSONSourceSpecification;
      return ''; // Ritorna stringa vuota per indicare fallimento
    }

    const newLayer: LayerBase = {
      id: newLayerId,
      name,
      visible: true,
      maplibreLayerType: maplibreLayerType,
      format: originalFormat,
      originalFormat: originalFormat,
      availablePropertyKeys: availableKeys,
      maplibreSourceId: maplibreSourceId ?? (sourceSpecification?.type === 'geojson' ? newLayerId : undefined),
      extent: extent,
      maplibreLayerId: newLayerId,
      sourceSpecification: sourceSpecification,
    };

    set((state) => ({
      dataLayers: [...state.dataLayers, newLayer],
    }));

    get().setLayerStyleDefaults(newLayerId, maplibreLayerType);
    get().zoomToLayerExtent(newLayerId);
    return newLayerId;
  },
  removeLayer: (id: string) => {
     set((state) => ({
       dataLayers: state.dataLayers.filter((layer) => layer.id !== id),
     }));
     get().removeLayerStyles(id);
     get().closeAttributeTable(id);
   },
  clearLayers: () => {
    const layerIds = get().dataLayers.map(l => l.id);
    layerIds.forEach(id => {
        get().removeLayerStyles(id);
    });
    get().closeAllAttributeTables();
    set({ dataLayers: [] });
  },
  toggleLayerVisibility: (layerId: string) => {
      set((state) => ({
       dataLayers: state.dataLayers.map((layer) =>
         layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
       ),
     }));
   },
  setLayerName: (layerId: string, name: string) =>
    set((state) => ({
      dataLayers: state.dataLayers.map((layer) =>
        layer.id === layerId ? { ...layer, name: name } : layer
      ),
    })),
  setSelectedLayerId: (layerId: string | null) => {
    set({
      selectedLayerId: layerId,
    });
    if (layerId !== null) {
      get().setActiveRightPanelTab('style');
    }
  },
  reorderLayers: (startIndex: number, endIndex: number) => {
    set((state) => {
      if (
        startIndex < 0 || startIndex >= state.dataLayers.length ||
        endIndex < 0 || endIndex >= state.dataLayers.length
      ) {
        console.error("Indici di riordino non validi:", startIndex, endIndex);
        return state;
      }
      const layers = [...state.dataLayers];
      const [removed] = layers.splice(startIndex, 1);
      if (removed) {
        layers.splice(endIndex, 0, removed);
      }
      return { dataLayers: layers };
    });
  },
  getLayerById: (id: string) => {
    const layer = get().dataLayers.find((layer) => layer.id === id);
    return layer ? layer : null;
  },
  updateLayerProperties: (payload: { id: string; properties: Partial<LayerBase> }) => {
    set((state) => {
      const newDataLayers = state.dataLayers.map((layer) => {
        if (layer.id === payload.id) {
          const newProps = { ...payload.properties };
          if (newProps.sourceSpecification) {
            newProps.sourceSpecification = cleanSourceOptions(newProps.sourceSpecification) as SourceSpecificationUnion;
          }
          return { ...layer, ...newProps };
        }
        return layer;
      });
      return { dataLayers: newDataLayers };
    });
  },
  addRasterSourceAndLayer: (params: {
    id: string;
    name: string;
    sourceSpecification: RasterSourceSpecification | RasterDemSourceSpecification;
    initialPaintProperties?: { rasterOpacity?: number };
  }): string => {
    const {
      id: sourceId,
      name,
      sourceSpecification,
      initialPaintProperties,
    } = params;

    if (!sourceSpecification.url && (!sourceSpecification.tiles || sourceSpecification.tiles.length === 0)) {
      console.error('Errore: Per aggiungere una sorgente raster, sourceSpecification deve contenere un url (TileJSON) o un array di tiles.');
      return '';
    }

    const layerId = `${sourceId}-layer`;

    // Assicurati che tileSize sia definito se non presente, default a 256 o 512.
    // MapLibre default è 512. Per coerenza con le opzioni UI, usiamo 256 se non specificato.
    const finalSourceSpec = {
        tileSize: 256, // Default se non fornito
        ...sourceSpecification,
    };


    const newRasterLayer: LayerBase = {
      id: layerId,
      name,
      data: undefined,
      visible: true,
      maplibreLayerType: 'raster',
      availablePropertyKeys: [],
      originalFormat: 'raster',
      format: 'raster',
      maplibreSourceId: sourceId,
      maplibreLayerId: layerId,
      sourceSpecification: finalSourceSpec, // Usa finalSourceSpec
      minzoom: finalSourceSpec.minzoom,
      maxzoom: finalSourceSpec.maxzoom,
      extent: finalSourceSpec.bounds,
    };

    set((state) => ({
      dataLayers: [...state.dataLayers, newRasterLayer],
    }));

    get().setLayerStyleDefaults(layerId, 'raster');

    if (initialPaintProperties && initialPaintProperties.rasterOpacity !== undefined) {
      get().updateLayerStyleProperty(
        layerId,
        'rasterOpacity',
        initialPaintProperties.rasterOpacity
      );
    }
    
    get().zoomToLayerExtent(layerId);
    return layerId;
  },
  addVectorSourceAndLayer: (params: {
    id: string;
    name: string;
    sourceSpecification: VectorSourceSpecification | GeoJSONSourceSpecification;
    maplibreLayerType: LayerBase['maplibreLayerType'];
    sourceLayer?: string;
    calculatedBoundsForZoom?: BBox;
  }): string => {
    const {
      id: sourceId,
      name,
      sourceSpecification,
      maplibreLayerType,
      sourceLayer,
      calculatedBoundsForZoom,
    } = params;

    if (sourceSpecification.type === 'vector' && !sourceSpecification.url && (!sourceSpecification.tiles || sourceSpecification.tiles.length === 0)) {
      console.error('Errore: Per aggiungere una sorgente vettoriale di tipo "vector", sourceSpecification deve contenere un url (TileJSON) o un array di tiles (XYZ).');
      return '';
    }
    if (sourceSpecification.type === 'geojson' && !sourceSpecification.data && !(sourceSpecification as any).url) {
      console.error('Errore: Per aggiungere una sorgente vettoriale di tipo "geojson", sourceSpecification.data deve essere fornito.');
      return '';
    }

    const layerId = `${sourceId}-layer`;

    const newVectorLayer: LayerBase = {
      id: layerId,
      name,
      data: undefined,
      visible: true,
      maplibreLayerType: maplibreLayerType,
      availablePropertyKeys: sourceSpecification.type === 'geojson' && typeof sourceSpecification.data === 'object'
                              ? extractPropertyKeys(sourceSpecification.data as GeoJSON.FeatureCollection)
                              : [],
      originalFormat: sourceSpecification.type,
      format: sourceSpecification.type,
      maplibreSourceId: sourceId,
      maplibreLayerId: layerId,
      sourceSpecification: sourceSpecification,
      minzoom: sourceSpecification.minzoom,
      maxzoom: sourceSpecification.maxzoom,
      sourceLayer: sourceLayer,
      extent: calculatedBoundsForZoom
              || (sourceSpecification.type === 'vector' ? sourceSpecification.bounds : undefined),
    };

    set((state) => ({
      dataLayers: [...state.dataLayers, newVectorLayer],
    }));

    get().setLayerStyleDefaults(layerId, newVectorLayer.maplibreLayerType);
    get().zoomToLayerExtent(layerId);
    return layerId;
  },
  setLayerAvailablePropertyKeys: (payload: LayerPropertyKeysPayload) => {
    set((state) => ({
      dataLayers: state.dataLayers.map((layer) =>
        layer.id === payload.layerId
          ? { ...layer, availablePropertyKeys: payload.keys }
          : layer
      ),
    }));
  },
  // NUOVA AZIONE
  updateLayerSourceTileSize: (layerId: string, tileSize: number) => {
    set((state) => {
      const layerIndex = state.dataLayers.findIndex((l) => l.id === layerId);
      if (layerIndex === -1) {
        console.warn(`[layerSlice/updateLayerSourceTileSize] Layer con ID ${layerId} non trovato.`);
        return state; // Ritorna lo stato invariato
      }

      const layers = [...state.dataLayers]; // Crea una copia dell'array dei layer
      const layerToUpdate = { ...layers[layerIndex] }; // Clona l'oggetto layer da aggiornare

      if (layerToUpdate.sourceSpecification &&
          (layerToUpdate.sourceSpecification.type === 'raster' || layerToUpdate.sourceSpecification.type === 'raster-dem')) {
        
        // Clona la sourceSpecification per l'immutabilità e per creare un nuovo oggetto
        const originalSourceSpec = layerToUpdate.sourceSpecification as RasterSourceSpecification | RasterDemSourceSpecification;
        
        const updatedSourceSpec = {
          ...originalSourceSpec, // Copia tutte le proprietà esistenti
          tileSize: tileSize,     // Aggiorna tileSize
        };
        
        // Assegna la nuova sourceSpecification clonata e aggiornata al layer clonato
        layerToUpdate.sourceSpecification = updatedSourceSpec;
        
        // Sostituisci il vecchio oggetto layer con il nuovo oggetto layer aggiornato nell'array clonato
        // Questo è cruciale per la reattività di React/Zustand e per far sì che
        // gli hook di sincronizzazione (come useLayerSynchronization) rilevino una modifica "significativa"
        // che potrebbe richiedere la rimozione e ri-aggiunta della sorgente in MapLibre.
        layers[layerIndex] = layerToUpdate;

        // console.log(`[layerSlice/updateLayerSourceTileSize] Layer ${layerId} aggiornato. TileSize: ${tileSize}.`);
        return { ...state, dataLayers: layers }; // Ritorna lo stato aggiornato con l'array di layer modificato
      } else {
        console.warn(`[layerSlice/updateLayerSourceTileSize] Layer con ID ${layerId} non è di tipo raster/raster-dem valido o manca sourceSpecification.`);
        return state; // Ritorna lo stato invariato
      }
    });
  },
});

// Selettore per ottenere solo i layer visibili
export const selectVisibleLayers = (state: GeoDataState): LayerBase[] => {
  return state.dataLayers.filter((layer: LayerBase) => layer.visible);
};

// Selettore per ottenere tutti i layer dell'applicazione
export const selectAllAppLayers = (state: GeoDataState): LayerBase[] => {
  return state.dataLayers;
};